import React, { useEffect, useMemo, useState } from 'react';
import { Chevron } from '../../components/Glyph';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../components/AuthContext';
import { useToast } from '../../components/ToastContext';
import { logAudit } from '../../hooks/useAudit';
import PageBar, { SearchInput } from '../../components/PageBar';
import ListGrid from '../../components/ListGrid';
import Dialog from '../../components/Dialog';
import { IconFilter } from '../../components/dialogIcons';
import { SheetPill } from '../../components/Pill';
import useMediaQuery from '../../hooks/useMediaQuery';
import {
  REPORTS, DRAW_CAP, applyFilters, bandsOf, csvFor, downloadCsv, exportLabel,
  inReadingOrder, isBlank, loadLines, locationOf, moreSentence, rollUp, summarise,
} from './reportData';

/*
  A report that shows the rows it found.

  **Built 6 September 2026 to Group F's approved drawings.** The owner looked at
  two shapes on 4 September and said "I have reviewed group f and I want shape
  B", and this is shape B: the filters in the bar, the totals on hairlines, the
  rows underneath, and a button that says how many rows it is about to write.

  One screen serves all three count reports, because they are one screen with
  one word changed. **Count History bands its rows under the location** and the
  other two carry the location as a column, which is the one real difference:
  Count History is read down one building at a time, and the other two are read
  across buildings, because their question is which buildings an item is sitting
  in.
*/
export default function CountReport({ kind }) {
  const spec = REPORTS[kind];
  const toast = useToast();
  const { profile } = useAuth();
  const wide = useMediaQuery('(min-width: 701px)');

  const [cycles, setCycles]       = useState([]);
  const [regions, setRegions]     = useState([]);
  const [places, setPlaces]       = useState([]);
  const [pickedCycles, setPickedCycles]   = useState([]);
  const [pickedRegions, setPickedRegions] = useState([]);
  const [pickedPlaces, setPickedPlaces]   = useState([]);
  const [search, setSearch] = useState('');
  const [lines, setLines]   = useState(null);
  const [open, setOpen]     = useState(null);   // which filter dialog is showing
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    supabase.from('count_cycles').select('id, name, status').order('created_at', { ascending: false })
      .then(({ data }) => {
        setCycles(data || []);
        if (data?.length) setPickedCycles([data[0].id]);
      });
    supabase.from('regions').select('id, name').order('name').then(({ data }) => setRegions(data || []));
    supabase.from('warehouses').select('id, name, display_name, region_prefix')
      .eq('is_do_not_use', false).order('name')
      .then(({ data }) => setPlaces(data || []));
  }, []);

  useEffect(() => {
    let live = true;
    setLines(null);
    (async () => {
      const { rows, error } = await loadLines(kind, pickedCycles);
      if (!live) return;
      if (error) { toast.error('Could not read the count: ' + error.message); setLines([]); return; }
      setLines(rollUp(rows));
    })();
    return () => { live = false; };
    // eslint-disable-next-line
  }, [kind, pickedCycles.join(',')]);

  const matched = useMemo(
    () => (lines === null ? [] : applyFilters(lines, {
      regionNames: pickedRegions, locationIds: pickedPlaces, search,
    })),
    [lines, pickedRegions, pickedPlaces, search],
  );
  const totals = useMemo(() => summarise(matched), [matched]);

  async function runExport() {
    if (!matched.length) return;
    const cycleName = pickedCycles.length === 1
      ? (cycles.find(c => c.id === pickedCycles[0])?.name || 'export')
      : 'multi-cycle';
    downloadCsv(csvFor(kind, matched), spec.file + '_' + cycleName.replace(/\s/g, '_'));
    await logAudit(profile, AUDIT_ACTION[kind], 'export', {
      details: { count: matched.length, cycles: pickedCycles, regions: pickedRegions, locations: pickedPlaces },
    });
    toast.success('Exported ' + matched.length.toLocaleString() + ' rows');
  }

  const filterCount = pickedCycles.length + pickedRegions.length + pickedPlaces.length;

  const chips = (
    <>
      <Chip label="Cycles" n={pickedCycles.length} onClick={() => setOpen('cycles')} />
      <Chip label="All regions" n={pickedRegions.length} onClick={() => setOpen('regions')} />
      <Chip label="All locations" n={pickedPlaces.length} onClick={() => setOpen('places')} />
    </>
  );

  return (
    <>
      <PageBar
        title={spec.title}
        subtitle={spec.subtitle}
        controls={wide ? (
          <>
            <SearchInput value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search an item or a location" />
            {chips}
          </>
        ) : (
          <>
            {/* Below 700 the search is its own round button and the three
                filters become one chip. Rule 21: nothing is lost, it moves
                behind one tap. */}
            <button className="rp-icon" aria-label="Search" onClick={() => setSearchOpen(o => !o)}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.8" />
                <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
              </svg>
            </button>
            <Chip label="Filters" n={filterCount} onClick={() => setOpen('all')} />
          </>
        )}
        meta={lines === null ? 'Reading...' : totals.lines.toLocaleString() + ' rows'}
        actions={
          <button className="tb-btn tb-btn-primary" onClick={runExport} disabled={!matched.length}>
            {exportLabel(totals.lines)}
          </button>
        }
      />

      {!wide && searchOpen && (
        <div style={{ marginBottom: 12 }}>
          <SearchInput value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search an item or a location" style={{ width: '100%' }} autoFocus />
        </div>
      )}

      <Figures totals={totals} blankLabel={spec.blankLabel} />

      {lines === null ? (
        <div className="rplist" style={{ padding: 48, textAlign: 'center', color: 'var(--text-secondary)', fontSize: 13 }}>
          Reading the count...
        </div>
      ) : matched.length === 0 ? (
        <NothingMatched
          kind={kind}
          cycles={cycles} pickedCycles={pickedCycles}
          pickedRegions={pickedRegions} pickedPlaces={pickedPlaces} search={search}
          onClearRegions={() => setPickedRegions([])}
          onClearPlaces={() => setPickedPlaces([])}
          onClearSearch={() => setSearch('')}
        />
      ) : spec.banded ? (
        <BandedRows rows={matched} totals={totals} kind={kind} />
      ) : (
        <FlatRows rows={matched} totals={totals} kind={kind} />
      )}

      {open && (
        <FilterDialog
          which={open}
          onClose={() => setOpen(null)}
          cycles={cycles} regions={regions} places={places}
          pickedCycles={pickedCycles} setPickedCycles={setPickedCycles}
          pickedRegions={pickedRegions} setPickedRegions={setPickedRegions}
          pickedPlaces={pickedPlaces} setPickedPlaces={setPickedPlaces}
          rowCount={totals.lines}
        />
      )}
    </>
  );
}

const AUDIT_ACTION = {
  count_history: 'EXPORT_COUNT_HISTORY',
  non_inventory: 'EXPORT_NON_INVENTORY',
  dead_stock:    'EXPORT_DEAD_STOCK',
};

/* Rule 5: the live numbers on their own strip, on hairlines, above the rows. */
function Figures({ totals, blankLabel }) {
  return (
    <div className="rpsum">
      <Figure n={totals.lines} label="Lines" />
      <Figure n={totals.locations} label="Locations" />
      <Figure n={totals.items} label="Items" />
      <Figure n={totals.units} label="Units counted" />
      <Figure n={totals.blank} label={blankLabel} red={totals.blank > 0} />
    </div>
  );
}

function Figure({ n, label, red }) {
  return (
    <span className="rpsum-c">
      <b className={'rpsum-n' + (red ? ' red' : '')}>{n.toLocaleString()}</b>
      <span className="rpsum-l">{label}</span>
    </span>
  );
}

/* The count, or the fact that nobody has been to that shelf. Rule 9. */
function Qty({ row }) {
  if (isBlank(row)) {
    return <span style={{ color: 'var(--text-secondary)', fontSize: 10.5, fontWeight: 800, textTransform: 'uppercase', whiteSpace: 'nowrap' }}>Not counted</span>;
  }
  return <b style={{ fontWeight: 800 }}>{row.quantity.toLocaleString()}</b>;
}

function Was({ row }) {
  const p = row.previous_quantity;
  if (p === null || p === undefined) return <span style={{ color: 'var(--text-secondary)' }}>-</span>;
  return <span style={{ color: 'var(--text-secondary)', fontWeight: 700 }}>{p.toLocaleString()}</span>;
}

const stateOf = row => (isBlank(row) ? 'state-blank' : 'state-counted');

/*
  Count History, banded. The location is a band across the rows rather than a
  column, because this report is read down one building at a time.
*/
function BandedRows({ rows, totals, kind }) {
  const bands = bandsOf(rows);
  let drawn = 0;
  const shown = [];
  for (const b of bands) {
    if (drawn >= DRAW_CAP) break;
    const take = b.rows.slice(0, DRAW_CAP - drawn);
    drawn += take.length;
    shown.push({ ...b, rows: take });
  }
  const more = moreSentence(kind, drawn, shown.length, totals);
  return (
    <div className="rplist">
      <ListGrid
        layoutKey="count_history"
        layoutTitle="Count History's columns"
        columns={[
          { label: 'Item number', width: '150px' },
          { label: 'Description', width: 'minmax(0,1fr)' },
          { label: 'Vendor',      width: '170px' },
          { label: 'Count',       width: '92px', align: 'right' },
          { label: 'Last count',  width: '100px', align: 'right' },
        ]}
        /* One table, banded, rather than one table per location. Drawn as
           separate tables it would have been one Columns button per band, each
           with its own copy of the arrangement. */
        groups={shown.map(b => ({
          key: b.id,
          rows: b.rows,
          header: (
            <div className="rpband">
              <span className="rpband-t">{b.code}</span>
              <span className="rpband-n">{b.name}</span>
              <span style={{ flex: 1 }} />
              <span className="rpband-s">{b.sheet?.rep?.full_name || ''}</span>
              <SheetPill status={b.sheet?.status} />
            </div>
          ),
        }))}
        keyOf={r => r.id}
        rowClass={stateOf}
        cells={r => [
          <span className="rpnum">{r.item_number_raw}</span>,
          <span className="rpdesc">{r.description_raw}</span>,
          <span className="rpvend">{r.vendor_raw || '-'}</span>,
          <Qty row={r} />,
          <Was row={r} />,
        ]}
        stack={r => (
          <span>
            <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <span className="rpnum">{r.item_number_raw}</span>
              <span style={{ textAlign: 'right' }}>
                <Qty row={r} />
                {r.previous_quantity !== null && r.previous_quantity !== undefined && (
                  <span style={{ display: 'block', fontSize: 11, fontWeight: 700, color: 'var(--text-secondary)' }}>
                    was {r.previous_quantity.toLocaleString()}
                  </span>
                )}
              </span>
            </span>
            <span style={{ display: 'block', fontSize: 12.5, marginTop: 3 }}>{r.description_raw}</span>
            <span className="rpvend" style={{ display: 'block', marginTop: 3 }}>{r.vendor_raw || '-'}</span>
          </span>
        )}
      />
      {more && <div className="rpmore">{more}</div>}
    </div>
  );
}

/*
  Non-Inventory Items and Dead Stock Items. The location is a column, because
  the question these two answer is which buildings an item is sitting in.
*/
function FlatRows({ rows, totals, kind }) {
  const ordered = inReadingOrder(rows).slice(0, DRAW_CAP);
  const more = moreSentence(kind, ordered.length, 0, totals);
  return (
    <div className="rplist">
      <ListGrid
        layoutKey={kind}
        layoutTitle={REPORTS[kind].title + "'s columns"}
        columns={[
          { label: 'Location',    width: '260px' },
          { label: 'Item number', width: '150px' },
          { label: 'Description', width: 'minmax(0,1fr)' },
          { label: 'Vendor',      width: '160px' },
          { label: 'Count',       width: '92px', align: 'right' },
          { label: 'Sheet',       width: '118px' },
        ]}
        rows={ordered}
        keyOf={r => r.id}
        rowClass={stateOf}
        cells={r => [
          <span className="rploc"><span className="rpband-t">{locationOf(r).code}</span> {locationOf(r).name}</span>,
          <span className="rpnum">{r.item_number_raw}</span>,
          <span className="rpdesc">{r.description_raw}</span>,
          <span className="rpvend">{r.vendor_raw || '-'}</span>,
          <Qty row={r} />,
          <SheetPill status={r.count?.status} />,
        ]}
        stack={r => (
          <span>
            <span style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 10 }}>
              <span className="rpnum">{r.item_number_raw}</span>
              <Qty row={r} />
            </span>
            <span style={{ display: 'block', fontSize: 12.5, marginTop: 3 }}>{r.description_raw}</span>
            <span style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 5, flexWrap: 'wrap' }}>
              <span className="rpband-t">{locationOf(r).code}</span>
              <span className="rpvend">{locationOf(r).name}</span>
              <SheetPill status={r.count?.status} />
            </span>
          </span>
        )}
      />
      {more && <div className="rpmore">{more}</div>}
    </div>
  );
}

/*
  **Nothing matched is a screen, not a silence.** The running app had no way to
  draw this at all: it never showed rows, so it never had to say there were
  none. It names what was filtered to and says plainly that exporting now would
  give you a file with its headings and nothing underneath.
*/
function NothingMatched({ cycles, pickedCycles, pickedRegions, pickedPlaces, search,
  onClearRegions, onClearPlaces, onClearSearch }) {
  const cycleNames = pickedCycles.map(id => cycles.find(c => c.id === id)?.name).filter(Boolean).join(', ');
  const bits = [];
  if (pickedRegions.length) bits.push(pickedRegions.join(', '));
  if (pickedPlaces.length)  bits.push(pickedPlaces.length + ' location' + (pickedPlaces.length === 1 ? '' : 's'));
  if (search.trim())        bits.push('"' + search.trim() + '"');
  return (
    <div className="rpnone">
      <div className="rpnone-i">
        <svg width="20" height="20" viewBox="0 0 16 16" fill="none">
          <circle cx="7" cy="7" r="5" stroke="currentColor" strokeWidth="1.8" />
          <path d="M11 11l3.5 3.5" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" />
        </svg>
      </div>
      <div className="rpnone-t">Nothing matched these filters</div>
      <div className="rpnone-s">
        {bits.length
          ? 'Nothing in ' + (cycleNames || 'the cycles you picked') + ' matches ' + bits.join(' and ') + '. '
          : 'Nothing in ' + (cycleNames || 'the cycles you picked') + ' has any lines yet. '}
        Exporting now would give you a file with its headings and nothing underneath.
      </div>
      <div className="rpnone-a">
        {pickedRegions.length > 0 && <button className="btn btn-quiet" onClick={onClearRegions}>Clear the region filter</button>}
        {pickedPlaces.length > 0 && <button className="btn btn-quiet" onClick={onClearPlaces}>Clear the location filter</button>}
        {search.trim() && <button className="btn btn-quiet" onClick={onClearSearch}>Clear the search</button>}
      </div>
    </div>
  );
}

function Chip({ label, n, onClick }) {
  return (
    <button className="rp-chip" onClick={onClick}>
      {label}{n > 0 && <span className="rp-chip-n">- {n}</span>}
      <Chevron size={11} className="rp-chip-c" />
    </button>
  );
}

/*
  A filter opens as a dialog on rule 34's shell rather than as a drop-down
  hanging off the bar. That is Group F's drawing and it is also the only shape
  that works below 1024, where the bar is not on screen at all.
*/
function FilterDialog({ which, onClose, cycles, regions, places,
  pickedCycles, setPickedCycles, pickedRegions, setPickedRegions,
  pickedPlaces, setPickedPlaces, rowCount }) {
  const all = which === 'all';
  const title = all ? 'Filters'
    : which === 'cycles' ? 'Which cycles?'
    : which === 'regions' ? 'Which regions?' : 'Which locations?';
  const says = all
    ? 'The report redraws as soon as you choose.'
    : which === 'cycles'
      ? 'The report redraws as soon as you choose.'
      : 'Leave everything unticked to see them all.';

  function clearAll() {
    if (all || which === 'cycles') setPickedCycles([]);
    if (all || which === 'regions') setPickedRegions([]);
    if (all || which === 'places')  setPickedPlaces([]);
  }

  return (
    <Dialog
      kind="info" icon={<IconFilter />}
      title={title}
      says={says}
      size="form"
      onClose={onClose}
      footer={
        <>
          <button className="btn btn-quiet" onClick={clearAll}>Clear</button>
          <button className="btn btn-primary" onClick={onClose}>
            Show {rowCount.toLocaleString()} row{rowCount === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      {(all || which === 'cycles') && (
        <Section title={all ? 'Cycles' : null}>
          {cycles.map(c => (
            <TickRow key={c.id} on={pickedCycles.includes(c.id)} label={c.name}
              side={c.status === 'open' ? 'OPEN' : 'CLOSED'}
              onToggle={() => setPickedCycles(p => p.includes(c.id) ? p.filter(x => x !== c.id) : [...p, c.id])} />
          ))}
        </Section>
      )}
      {(all || which === 'regions') && (
        <Section title={all ? 'Regions' : null}>
          {regions.map(r => (
            <TickRow key={r.id} on={pickedRegions.includes(r.name)} label={r.name}
              onToggle={() => setPickedRegions(p => p.includes(r.name) ? p.filter(x => x !== r.name) : [...p, r.name])} />
          ))}
        </Section>
      )}
      {(all || which === 'places') && (
        <Section title={all ? 'Locations' : null} scroll>
          {places.map(w => (
            <TickRow key={w.id} on={pickedPlaces.includes(w.id)}
              label={(w.region_prefix ? w.region_prefix + '  ' : '') + (w.display_name || w.name)}
              onToggle={() => setPickedPlaces(p => p.includes(w.id) ? p.filter(x => x !== w.id) : [...p, w.id])} />
          ))}
        </Section>
      )}
    </Dialog>
  );
}

function Section({ title, scroll, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      {title && (
        <div style={{ fontSize: 10, fontWeight: 800, letterSpacing: 1.3, textTransform: 'uppercase', color: 'var(--text-secondary)', margin: '0 0 6px' }}>
          {title}
        </div>
      )}
      <div className="rp-ticks" style={scroll ? { maxHeight: 240, overflowY: 'auto' } : undefined}>{children}</div>
    </div>
  );
}

function TickRow({ on, label, side, onToggle }) {
  return (
    <label className={'rp-tick' + (on ? ' on' : '')}>
      <input type="checkbox" checked={on} onChange={onToggle} />
      <span className="rp-tick-l">{label}</span>
      {side && <span className="rp-tick-s">{side}</span>}
    </label>
  );
}
